import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1693661391267-ad955aeeb564?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbmlnaHR8ZW58MHx8fHwxNzgzODEyNTY1fDA&ixlib=rb-4.1.0&q=85";

export default function EmergencyCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section
      ref={ref}
      data-testid="emergency-cta"
      className="relative overflow-hidden bg-black py-28 md:py-40"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 -z-10 will-change-transform"
      >
        <img
          src={BG}
          alt="Construction site at night"
          className="h-full w-full object-cover"
          style={{ filter: "grayscale(0.6) brightness(0.4)" }}
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-black/50" />
      <div className="pointer-events-none absolute inset-0 -z-10 grain" />

      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="eyebrow flex items-center gap-3">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--accent)] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--accent)]" />
          </span>
          Emergency line · Live now
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-8 font-oswald text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-[10vw]"
        >
          Storm damage?
          <br />
          <span className="text-[color:var(--accent)]">Burst pipe?</span>
          <br />
          Board-up?
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-10 grid gap-8 md:mt-14 md:grid-cols-[1.2fr_1fr] md:items-end"
        >
          <p className="max-w-xl font-editorial text-xl italic leading-snug text-white/80 md:text-2xl">
              We answer our own phone &mdash; 24 hours, 7 days, every holiday.
            Residential or commercial, one call, one crew.
          </p>

          <a
            href="tel:6103497313"
            data-testid="emergency-call-btn"
            className="group inline-flex flex-col items-start gap-2 self-end border border-[color:var(--accent)] bg-[color:var(--accent)] px-8 py-6 text-[color:var(--accent-ink)] transition-transform duration-500 hover:-translate-y-1 md:justify-self-end"
          >
            <span className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em]">
              <Phone size={14} /> Call now — 24/7
            </span>
            <span className="font-oswald text-4xl leading-none tracking-tight md:text-5xl">
              610-349-7313
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
